import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Plus, MessageSquare, Search, SlidersHorizontal, ArrowRightLeft, Download } from "lucide-react";
import * as XLSX from "xlsx";
import EnquiryTableRow from "./EnquiryTableRow";
import { PageHeader, EmptyState, AppButton, AppCard, AppCardContent, ConfirmModal, FilterModal, FilterGroup } from "../ui";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import { useSocket } from "../../contexts/SocketContext";
import { playEnquiryNotificationSound } from "../../utils/notificationSound";
import { useBranch } from "../../contexts/BranchContext";
import { getCurrentUser } from "../../utils/auth";
import TransferBranchModal from "../Common/TransferBranchModal";
import "./enquiries-list.scss";

const ENQUIRY_STATUS = [
  "Enquired",
  "Confirmed",
  "Dropped",
  "Postponed",
  "Closed",
  "Converted"
];

const ENTRIES_PER_PAGE = 10;
const FILTER_STORAGE_KEY = 'eq_filters_v1';

function getDefaultFilters() {
  const user = getCurrentUser();
  const isAdmin = user?.usertype === 'admin' || user?.usertype === 'superadmin';
  return {
    search: "",
    status: "",
    course: "",
    mobile: "",
    startDate: "",
    endDate: "",
    source: "",
    campaign: "",
    assignedTo: isAdmin && user?.adminId ? String(user.adminId) : "",
  };
}

function loadFilters() {
  try {
    const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch { /* ignore */ }
  return getDefaultFilters();
}

const EnquiriesList = () => {
  const [filters, setFilters] = useState(loadFilters);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [courses, setCourses] = useState([{ value: "", label: "All Courses" }]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [showBranchTransfer, setShowBranchTransfer] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const { selectedBranchId, isMultiBranch, branches } = useBranch();
  const currentUser = getCurrentUser();

  const navigate = useNavigate();

  // Persist filters to sessionStorage whenever they change
  useEffect(() => {
    try { sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters)); } catch { /* ignore */ }
  }, [filters]);

  const activeFilterCount = [filters.status, filters.course, filters.mobile, filters.startDate, filters.endDate, filters.source, filters.campaign, filters.assignedTo].filter(Boolean).length;

  const handleResetFilters = () => {
    setFilters((f) => ({ ...f, status: "", course: "", mobile: "", startDate: "", endDate: "", source: "", campaign: "", assignedTo: "" }));
    setShowFilterModal(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {
          ...filters,
          page: currentPage,
          limit: ENTRIES_PER_PAGE,
        };
        const [{ data: eData }, { data: cData }] = await Promise.all([
          apiClient.get("/enquiries/list", { params }),
          apiClient.get("/courses/list"),
        ]);
        setEnquiries(eData.enquiries || []);
        setTotalPages(eData.totalPages || 1);
        setTotalRecords(eData.total || 0);
        setCourses([
          { value: "", label: "All Courses" },
          ...(cData.courses || []).map((c) => ({
            value: c._id,
            label: c.name,
          })),
        ]);
      } catch {
        setToast({
          show: true,
          message: "Failed to load enquiries.",
          type: "error",
        });
      }
      setLoading(false);
    };
    fetchData();
    // eslint-disable-next-line
  }, [filters, currentPage, selectedBranchId, refreshKey]);

  // Fetch assignees list for the filter dropdown
  useEffect(() => {
    apiClient.get("/enquiries/assignees")
      .then(({ data }) => setAssignees(data.admins || []))
      .catch(() => {});
  }, []);

  // ── Live-reload when a new enquiry arrives via socket ──
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;
    const handleNewEnquiry = (data) => {
      if (data.type === 'new_enquiry') {
        playEnquiryNotificationSound();
        setToast({ show: true, message: `New lead: ${data.fullName || 'Unknown'}`, type: "success" });
        // Trigger re-fetch by bumping refreshKey
        setRefreshKey(k => k + 1);
      }
    };
    socket.on('notification', handleNewEnquiry);
    return () => socket.off('notification', handleNewEnquiry);
  }, [socket]);

  // Re-fetch when refreshKey changes (new enquiry arrived)
  useEffect(() => {
    if (refreshKey === 0) return; // skip initial
    const reFetch = async () => {
      try {
        const params = { ...filters, page: currentPage, limit: ENTRIES_PER_PAGE };
        const { data: eData } = await apiClient.get("/enquiries/list", { params });
        setEnquiries(eData.enquiries || []);
        setTotalPages(eData.totalPages || 1);
        setTotalRecords(eData.total || 0);
      } catch { /* silent */ }
    };
    reFetch();
    // eslint-disable-next-line
  }, [refreshKey]);

  // Reset to page 1 if filters change (except currentPage)
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line
  }, [JSON.stringify(filters)]);

  const handleDelete = async (id) => {
    if (!id) return;
    setDeleteTarget(id);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/enquiries/${deleteTarget}`);
      setToast({
        show: true,
        message: "Enquiry deleted successfully!",
        type: "success",
      });
      setRefreshKey(k => k + 1);
    } catch {
      setToast({
        show: true,
        message: "Failed to delete enquiry.",
        type: "error",
      });
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = { ...filters, page: 1, limit: 10000 };
      const { data } = await apiClient.get("/enquiries/list", { params });
      const rows = (data.enquiries || []).map((e) => ({
        ID: e._id,
        Name: e.fullName || "",
        Email: e.email || "",
        Mobile: e.mobile || "",
        Course: e.course?.name || "",
        Status: e.status || "",
        Source: e.leadSource || e.source || "",
        Campaign: e.campaign?.name || "",
        "Assigned To": e.assignedTo
            ? `${e.assignedTo.firstName || ''} ${e.assignedTo.lastName || ''}`.trim() || e.assignedTo.email || ''
            : "",
        Branch: e.branchId?.name || "",
        Notes: Array.isArray(e.followUpNotes)
          ? e.followUpNotes.map((n) => `${n.note}`).join(" | ")
          : "",
        "Created At": e.createdAt ? new Date(e.createdAt).toLocaleString() : "",
      }));

      if (rows.length === 0) {
        setToast({ show: true, message: "No enquiries to export.", type: "info" });
        return;
      }

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Enquiries");
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `enquiries-${stamp}.xlsx`);
      setToast({ show: true, message: `Exported ${rows.length} enquiries.`, type: "success" });
    } catch {
      setToast({ show: true, message: "Failed to export enquiries.", type: "error" });
    } finally {
      setExporting(false);
    }
  };

  function getPaginationNumbers(current, total) {
    const pages = [];
    if (total <= 5) {
      for (let i = 1; i <= total; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    if (current > 3) pages.push("...");
    for (
      let i = Math.max(2, current - 1);
      i <= Math.min(total - 1, current + 1);
      i++
    ) {
      pages.push(i);
    }
    if (current < total - 2) pages.push("...");
    pages.push(total);
    return pages;
  }

  return (
    <div className="enquiries-page">
      {/* Page Header */}
      <PageHeader
        title="Enquiries"
        description={`Manage ${totalRecords} enquiries from prospective students`}
        icon={MessageSquare}
        actions={
          <>
            <AppButton variant="secondary" onClick={handleExport} disabled={exporting || loading}>
              {exporting ? <Spinner animation="border" size="sm" /> : <Download size={16} />}
              {exporting ? " Exporting..." : " Export"}
            </AppButton>
            <AppButton onClick={() => navigate("/enquiries/new")}>
              <Plus size={16} /> Add Enquiry
            </AppButton>
          </>
        }
      />

      {/* Toolbar: Search + Filters */}
      <div className="eq-toolbar">
        <div className="eq-search">
          <Search size={16} className="eq-search__icon" />
          <input
            type="text"
            className="eq-search__input"
            placeholder="Search name, email..."
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          />
        </div>

        <div className="eq-toolbar-actions">
          {/* Desktop inline filters */}
          <select
            className="eq-filter-select d-none d-md-block"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="">All Status</option>
            {ENQUIRY_STATUS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            className="eq-filter-select d-none d-md-block"
            value={filters.course}
            onChange={(e) => setFilters((f) => ({ ...f, course: e.target.value }))}
          >
            {courses.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <select
            className="eq-filter-select d-none d-md-block"
            value={filters.source}
            onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}
          >
            <option value="">All Sources</option>
            <option value="manual">Manual</option>
            <option value="website">Website</option>
            <option value="embed_form">Embed Form</option>
            <option value="google_ads">Google Ads</option>
            <option value="meta_ads">Meta Ads</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="zoho">Zoho CRM</option>
            <option value="all_except_zoho">Except Zoho</option>
            <option value="other">Other</option>
          </select>
          <select
            className="eq-filter-select d-none d-md-block"
            value={filters.assignedTo}
            onChange={(e) => setFilters((f) => ({ ...f, assignedTo: e.target.value }))}
          >
            <option value="">All Assignees</option>
            <option value="unassigned">Unassigned</option>
            {assignees.map((a) => {
              const name = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email;
              const isMe = currentUser?.adminId && String(a._id) === String(currentUser.adminId);
              return (
                <option key={a._id} value={a._id}>
                  {isMe ? `${name} (Me)` : name}
                </option>
              );
            })}
          </select>

          {/* Mobile filter button */}
          <button className="eq-filter-btn d-md-none" onClick={() => setShowFilterModal(true)}>
            <SlidersHorizontal size={16} />
            {activeFilterCount > 0 && <span className="eq-filter-badge">{activeFilterCount}</span>}
          </button>
        </div>

        {selected.length > 0 && isMultiBranch && branches.length > 1 && (
          <AppButton
            variant="secondary"
            size="sm"
            onClick={() => setShowBranchTransfer(true)}
          >
            <ArrowRightLeft size={14} /> Change Branch ({selected.length})
          </AppButton>
        )}
      </div>

      {/* Mobile Filter Modal */}
      <FilterModal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        onReset={handleResetFilters}
      >
        <FilterGroup label="Status">
          <select
            className="form-select"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="">All Status</option>
            {ENQUIRY_STATUS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </FilterGroup>
        <FilterGroup label="Course">
          <select
            className="form-select"
            value={filters.course}
            onChange={(e) => setFilters((f) => ({ ...f, course: e.target.value }))}
          >
            {courses.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </FilterGroup>
        <FilterGroup label="Mobile">
          <input
            type="text"
            className="form-control"
            placeholder="Mobile number"
            value={filters.mobile}
            onChange={(e) => setFilters((f) => ({ ...f, mobile: e.target.value }))}
          />
        </FilterGroup>
        <FilterGroup label="From Date">
          <input
            type="date"
            className="form-control"
            value={filters.startDate}
            onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
          />
        </FilterGroup>
        <FilterGroup label="To Date">
          <input
            type="date"
            className="form-control"
            value={filters.endDate}
            onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
          />
        </FilterGroup>
        <FilterGroup label="Source">
          <select
            className="form-select"
            value={filters.source}
            onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}
          >
            <option value="">All Sources</option>
            <option value="manual">Manual</option>
            <option value="website">Website</option>
            <option value="embed_form">Embed Form</option>
            <option value="google_ads">Google Ads</option>
            <option value="meta_ads">Meta Ads</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="zoho">Zoho CRM</option>
            <option value="all_except_zoho">Except Zoho</option>
            <option value="other">Other</option>
          </select>
        </FilterGroup>
        <FilterGroup label="Campaign">
          <input
            type="text"
            className="form-control"
            placeholder="Campaign name or ID"
            value={filters.campaign}
            onChange={(e) => setFilters((f) => ({ ...f, campaign: e.target.value }))}
          />
        </FilterGroup>
        <FilterGroup label="Assigned To">
          <select
            className="form-select"
            value={filters.assignedTo}
            onChange={(e) => setFilters((f) => ({ ...f, assignedTo: e.target.value }))}
          >
            <option value="">All Assignees</option>
            <option value="unassigned">Unassigned</option>
            {assignees.map((a) => {
              const name = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email;
              const isMe = currentUser?.adminId && String(a._id) === String(currentUser.adminId);
              return (
                <option key={a._id} value={a._id}>
                  {isMe ? `${name} (Me)` : name}
                </option>
              );
            })}
          </select>
        </FilterGroup>
      </FilterModal>

      {/* Table */}
      <AppCard className="eq-table-card">
        <AppCardContent className="p-0">
          {loading ? (
            <div className="eq-loading">
              <Spinner animation="border" size="sm" />
              <span>Loading enquiries...</span>
            </div>
          ) : enquiries.length === 0 ? (
            <EmptyState
              icon={<MessageSquare size={40} />}
              title="No enquiries found"
              description="Try adjusting your search or filter criteria"
            />
          ) : (
            <div className="eq-table-wrapper">
              <table className="eq-table">
                <thead>
                  <tr>
                    {isMultiBranch && branches.length > 1 && (
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          checked={selected.length === enquiries.length && enquiries.length > 0}
                          onChange={(e) =>
                            setSelected(e.target.checked ? enquiries.map((eq) => eq._id) : [])
                          }
                        />
                      </th>
                    )}
                    <th>ID</th>
                    <th>Student Name</th>
                    <th>Course</th>
                    <th>Contact</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Assigned</th>
                    <th>Notes</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {enquiries.map((enquiry) => (
                    <EnquiryTableRow
                      key={enquiry._id}
                      enquiry={enquiry}
                      onDelete={handleDelete}
                      onNoteAdded={() => setRefreshKey(k => k + 1)}
                      onConverted={() => {
                        setToast({ show: true, message: "Enquiry moved to Students successfully!", type: "success" });
                        setRefreshKey(k => k + 1);
                      }}
                      selectable={isMultiBranch && branches.length > 1}
                      selected={selected.includes(enquiry._id)}
                      onSelect={(id, checked) =>
                        setSelected((prev) =>
                          checked ? [...prev, id] : prev.filter((x) => x !== id)
                        )
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="eq-pagination">
              <span className="eq-pagination__info">
                Showing {totalRecords === 0 ? 0 : (currentPage - 1) * ENTRIES_PER_PAGE + 1} to{" "}
                {(currentPage - 1) * ENTRIES_PER_PAGE + enquiries.length} of {totalRecords}
              </span>
              <div className="eq-pagination__controls">
                <button
                  className="eq-pagination__btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  ‹
                </button>
                {getPaginationNumbers(currentPage, totalPages).map((num, idx) =>
                  num === "..." ? (
                    <span key={`ellipsis-${idx}`} className="eq-pagination__ellipsis">…</span>
                  ) : (
                    <button
                      key={`page-${num}`}
                      className={`eq-pagination__btn${num === currentPage ? " active" : ""}`}
                      onClick={() => setCurrentPage(num)}
                    >
                      {num}
                    </button>
                  )
                )}
                <button
                  className="eq-pagination__btn"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Enquiry"
        message="Are you sure you want to delete this enquiry? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deleting}
      />

      <TransferBranchModal
        show={showBranchTransfer}
        onHide={() => setShowBranchTransfer(false)}
        selectedIds={selected}
        mode="enquiries"
        onTransfer={() => {
          setSelected([]);
          setRefreshKey(k => k + 1);
          setToast({ show: true, message: "Enquiries moved to new branch!", type: "success" });
        }}
      />
    </div>
  );
};

export default EnquiriesList;
